'use client';
import { getApiBaseUrl } from "@/lib/apiBase";
import { fetchJsonArray } from "@/lib/fetchJsonSafe";
import { CategoryGlyph } from "@/components/CategoryGlyph";
import Link from 'next/link';
import { useMemo, useState, useEffect, useRef } from "react";
import { usePathname, useRouter } from "next/navigation";
import Topbar from './Topbar';
import Image from "next/image";
import { FaUserCircle, FaSearch, FaBars } from "react-icons/fa";
import { User, LogOut, Settings } from "lucide-react";
import Sidebar from "./SideBar";
import { useSession, signOut } from "next-auth/react";
import { getPartnerPortalUrl } from "@/lib/partnerPortalUrl";
const BASE_URL = getApiBaseUrl();
const PARTNER_PORTAL = getPartnerPortalUrl();

async function parseJsonSafely(response, fallback = null) {
  try {
    const text = await response.text();
    if (!text?.trim()) return fallback;
    return JSON.parse(text);
  } catch {
    return fallback;
  }
}

export default function NavbarLogged() {
  const { data: session } = useSession();
  const [query, setQuery] = useState("");
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [districtQuery, setDistrictQuery] = useState("");
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [userImage, setUserImage] = useState(null);
  const searchInputRef = useRef(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const categoryMenuRef = useRef(null);
  const categoryMenuCloseTimerRef = useRef(null);

  const clearCategoryMenuCloseTimer = () => {
    if (categoryMenuCloseTimerRef.current) {
      clearTimeout(categoryMenuCloseTimerRef.current);
      categoryMenuCloseTimerRef.current = null;
    }
  };

  const scheduleCategoryMenuClose = () => {
    clearCategoryMenuCloseTimer();
    categoryMenuCloseTimerRef.current = setTimeout(() => {
      setHoveredCategory(null);
      categoryMenuCloseTimerRef.current = null;
    }, 140);
  };

  const router = useRouter();
  const pathname = usePathname();
  const [menuOpen, setMenuOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const navItems = useMemo(() => {
    if (session) {
      return [
        { href: "/review", label: "Сэтгэгдэл бичих" },
        { href: "/blog", label: "Блог" },
      ];
    }
    return [
      { href: "/review", label: "Сэтгэгдэл бичих" },
      { href: "/blog", label: "Блог" },
      { href: "/customer", label: "Нэвтрэх" },
    ];
  }, [session]);
  const menuRef = useRef(null);
  const dropdownRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [isSearchAreaActive, setIsSearchAreaActive] = useState(false);
  const [isSearchInputFocused, setIsSearchInputFocused] = useState(false);
  const blurTimeoutRef = useRef(null);
  const searchBlurTimeoutRef = useRef(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!isMounted) return;
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isMounted]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await fetchJsonArray(`/api/categories?all=1`);
        const normalizedCategories = data.map((cat) => ({
          ...cat,
          parent_id: cat.parent_id ?? null,
          companyCount: cat.companyCount ?? 0,
          order: cat.order ?? 0,
        }));
        setCategories(normalizedCategories);
      } catch {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const response = await fetch(`/api/district?type=district`);
        const data = await parseJsonSafely(response, []);
        setDistricts(Array.isArray(data) ? data : []);
      } catch {
        setDistricts([]);
      }
    };
    fetchDistricts();
  }, []);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const data = await fetchJsonArray(`/api/partner?type=partner`);
        setCompanies(data);
      } catch {
        setCompanies([]);
      }
    };
    fetchCompanies();
  }, []);


  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }

      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setQuery("");
        setIsSearchAreaActive(false);
      }

      // Don't close category menu on click outside when hovering
      // if (categoryMenuRef.current && !categoryMenuRef.current.contains(e.target)) {
      //   setHoveredCategory(null);
      // }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
      if (searchBlurTimeoutRef.current) {
        clearTimeout(searchBlurTimeoutRef.current);
      }
      clearCategoryMenuCloseTimer();
    };
  }, []);

  useEffect(() => {
    if (!isSearchAreaActive) {
      setDistrictSuggestions([]);
    }
  }, [isSearchAreaActive]);

  // Fetch user image when session is available
  useEffect(() => {
    const fetchUserImage = async () => {
      if (session?.id) {
        try {
          const userRes = await fetch(`/api/customer?type=detail&id=${session.id}`);
          const userData = await parseJsonSafely(userRes, null);
          if (userData?.image) {
            // If image is a path, add BASE_URL
            if (userData.image.startsWith('/')) {
              setUserImage(`${BASE_URL}${userData.image}`);
            } else if (userData.image.startsWith('http')) {
              setUserImage(userData.image);
            } else {
              setUserImage(`${BASE_URL}/${userData.image}`);
            }
          }
        } catch (error) {
          console.error('Error fetching user image:', error);
        }
      } else {
        setUserImage(null);
      }
    };
    fetchUserImage();
  }, [session]);

  // Build category tree (parent_id null = root; siblings sorted by order)
  const categoryTree = useMemo(() => {
    const parentMap = {};
    categories.forEach((cat) => {
      const parentKey = cat.parent_id != null ? cat.parent_id : null;
      if (!parentMap[parentKey]) parentMap[parentKey] = [];
      parentMap[parentKey].push(cat);
    });
    Object.keys(parentMap).forEach((k) => {
      parentMap[k].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    });
    return parentMap;
  }, [categories]);

  const handleShowAll = (search, district) => {
    router.push(`/partner/partnersearch?search=${search}&district=${district || ""}`);
  };

  const handleSearch = () => {
    handleShowAll(query, selectedDistrict?.id);
    setDistrictSuggestions([]);
  };

  const handleDistrictSelect = (district) => {
    if (!district) return;
    setSelectedDistrict(district);
    setDistrictQuery(district.name);
    setDistrictSuggestions([]);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
    setTimeout(() => {
      setIsSearchInputFocused(true);
      setIsSearchAreaActive(true);
    }, 0);
  };

const filtered = useMemo(() => {
  const q = query.trim().toLowerCase();
  const d = selectedDistrict?.id;

  return companies.filter((c) => {
    const nameStr = (c.company_name || c.companyName || c.name || "").toLowerCase();
    const matchName = !q || nameStr.includes(q);
    const distId = c.district ?? c.sumDuuregId ?? "";
    const matchDistrict = !d || String(distId) === String(d);
    return matchName && matchDistrict;
  });
}, [query, companies, selectedDistrict]);

  return (
    <>
      {pathname === "/" && <Topbar />}
      <hr className='text-[#577099]'></hr>
      <nav
        className={`text-black py-4 shadow-lg transition-all duration-300 bg-[#1f2837] ${
          isSticky ? "fixed top-0 left-0 w-full shadow-md z-50" : "relative"
        }`}
      >
        <div className="max-w-[1440px] mx-auto flex items-center justify-between gap-6">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <Image
              src="/chanaraas-logo.svg"
              alt="Chanaraas Logo"
              width={48}
              height={48}
              priority
            />
            <span className="text-2xl font-bold text-white tracking-tight">
              CHANARAAS
            </span>
          </Link>
          <div className="relative flex items-center gap-2">
            <div
              ref={categoryMenuRef}
              className="relative"
              onMouseEnter={() => {
                clearCategoryMenuCloseTimer();
                const roots = categoryTree[null];
                if (roots && roots.length > 0) {
                  setHoveredCategory((prev) => prev ?? roots[0].id);
                }
              }}
              onMouseLeave={scheduleCategoryMenuClose}
            >
              <button
                type="button"
                onClick={() => {
                  const roots = categoryTree[null];
                  if (roots && roots.length > 0) {
                    router.push(
                      `/partner/partnercategory?search=${encodeURIComponent(roots[0].id)}`
                    );
                  } else {
                    router.push("/partner/partnercategory");
                  }
                }}
                className="cursor-pointer text-white bg-[#577099]
                            flex items-center gap-1 px-3 py-3 border border-transparent 
                            rounded-lg shadow-sm transition-all duration-300 text-xs font-medium hover:shadow-md"
              >
                <FaBars size={14} />
                Ангилал
              </button>

              {/* Hover mega menu: бүхэл товч + доош нээгдэх хэсэг нэг контейнер дотор */}
              {hoveredCategory !== null && categoryTree[null] && categoryTree[null].length > 0 && (
                <div
                  className="absolute top-full left-0 z-[100] pt-1"
                  onMouseEnter={clearCategoryMenuCloseTimer}
                >
                  <div className="bg-white rounded-lg shadow-xl border border-gray-200 min-w-[760px]">
                  <div className="flex">
                    {/* Parent Categories List */}
                    <div className="border-r border-gray-200 min-w-[300px]">
                      {categoryTree[null].map((parent) => {
                        const isHovered = String(hoveredCategory) === String(parent.id);
                        return (
                          <Link
                            key={parent.id}
                            href={`/partner/partnercategory?search=${encodeURIComponent(parent.id)}`}
                            onMouseEnter={() => setHoveredCategory(parent.id)}
                            onClick={() => {
                              clearCategoryMenuCloseTimer();
                              setHoveredCategory(null);
                            }}
                            className={`px-4 py-3 cursor-pointer transition-colors whitespace-nowrap block ${
                              isHovered
                                ? "bg-[#577099] text-white"
                                : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <CategoryGlyph icon={parent.icon} size="md" />
                              <span className="text-xs font-medium">{parent.name}</span>
                              {parent.companyCount !== undefined && parent.companyCount > 0 && (
                                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                                  isHovered ? "text-white bg-white/20" : "text-blue-600 bg-blue-50"
                                }`}>({parent.companyCount})</span>
                              )}
                            </div>
                          </Link>
                        );
                      })}
                    </div>

                    {/* Sub Categories: дэд ангилал байхгүй ч эцгийн «Бүгдийг үзэх» харагдана */}
                    {hoveredCategory &&
                      categoryTree[null]?.some((p) => String(p.id) === String(hoveredCategory)) && (
                      <div className="flex-1 min-w-[260px] max-h-[400px] overflow-y-auto border-r border-gray-200">
                        {(() => {
                          const hoveredCategoryData = categoryTree[null]?.find(
                            (cat) => String(cat.id) === String(hoveredCategory)
                          );
                          const hoveredCount = hoveredCategoryData?.companyCount || 0;
                          return (
                            <Link
                              href={`/partner/partnercategory?search=${encodeURIComponent(hoveredCategory)}`}
                              className="block px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-[#577099] hover:text-white border-b border-gray-200 transition-colors"
                              onClick={() => {
                                clearCategoryMenuCloseTimer();
                                setHoveredCategory(null);
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <span>Бүгдийг үзэх</span>
                                {hoveredCount > 0 && (
                                  <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full group-hover:text-white group-hover:bg-white/20">({hoveredCount})</span>
                                )}
                              </div>
                            </Link>
                          );
                        })()}
                        {(categoryTree[hoveredCategory] || []).map((child) => {
                          const subChildren = categoryTree[child.id] || [];
                          // Show sub category even if companyCount is 0, but only show count if > 0
                          return (
                            <div key={child.id} className="border-b border-gray-100 last:border-b-0 group">
                              <Link
                                href={`/partner/partnercategory?search=${encodeURIComponent(child.id)}`}
                                className="block px-4 py-2.5 text-xs text-gray-700 hover:bg-[#577099] hover:text-white font-medium transition-colors"
                                onClick={() => {
                                  clearCategoryMenuCloseTimer();
                                  setHoveredCategory(null);
                                }}
                              >
                                <div className="flex items-center gap-2">
                                  <CategoryGlyph icon={child.icon} size="sm" />
                                  <span>{child.name}</span>
                                  {child.companyCount !== undefined && child.companyCount !== null && child.companyCount > 0 && (
                                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full group-hover:text-white group-hover:bg-white/20 text-blue-600 bg-blue-50 transition-colors">({child.companyCount})</span>
                                  )}
                                </div>
                              </Link>
                              {/* Sub-sub categories */}
                              {subChildren.length > 0 && (
                                <div className="pl-4">
                                  {subChildren.map((sub) => (
                                    <div key={sub.id} className="group">
                                      <Link
                                        href={`/partner/partnercategory?search=${encodeURIComponent(sub.id)}`}
                                        className="block px-4 py-2 text-xs text-gray-600 hover:bg-[#577099] hover:text-white transition-colors"
                                        onClick={() => {
                                          clearCategoryMenuCloseTimer();
                                          setHoveredCategory(null);
                                        }}
                                      >
                                        <div className="flex items-center gap-2">
                                          <CategoryGlyph icon={sub.icon} size="sm" />
                                          <span>{sub.name}</span>
                                          {sub.companyCount !== undefined && sub.companyCount !== null && sub.companyCount > 0 && (
                                            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full group-hover:text-white group-hover:bg-white/20 transition-colors">({sub.companyCount})</span>
                                          )}
                                        </div>
                                      </Link>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {/* Vertical Banner Sidebar - Changes based on hovered category */}
                    <div className="border-l border-gray-200 w-[200px] flex flex-col gap-2 p-3 bg-gray-50">
                      {(() => {
                        // Get the hovered category data
                        const hoveredCategoryData = hoveredCategory
                          ? categoryTree[null]?.find(
                              (cat) => String(cat.id) === String(hoveredCategory)
                            )
                          : null;
                        
                        // Get banner info from category (if available) or use default
                        const bannerImage = hoveredCategoryData?.banner_image || null;
                        const bannerLink = hoveredCategoryData?.banner_link || '#';
                        const bannerTitle = hoveredCategoryData?.banner_title || hoveredCategoryData?.name || 'Баннер';
                        
                        return (
                          <Link
                            href={bannerLink}
                            target={bannerLink !== '#' ? "_blank" : undefined}
                            rel={bannerLink !== '#' ? "noopener noreferrer" : undefined}
                            className="relative w-full h-[200px] rounded-lg overflow-hidden hover:opacity-90 transition-opacity flex items-center justify-center"
                          >
                            {bannerImage ? (
                              <>
                                <Image
                                  src={`${BASE_URL}${bannerImage}`}
                                  alt={bannerTitle}
                                  fill
                                  className="object-cover rounded-lg"
                                  sizes="200px"
                                />
                                <div className="absolute inset-0 bg-black/20 flex items-center justify-center p-2 rounded-lg">
                                  <span className="text-white text-xs font-semibold text-center drop-shadow-lg">
                                    {bannerTitle}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center rounded-lg">
                                <div className="text-center p-4">
                                  <span className="text-white text-sm font-semibold drop-shadow-lg">
                                    {bannerTitle}
                                  </span>
                                </div>
                              </div>
                            )}
                          </Link>
                        );
                      })()}
                    </div>
                  </div>
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} categories={categories} />
            {/* Overlay */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black/30 z-40"
                onClick={() => setSidebarOpen(false)}
              />
            )}
          </div>

          {/* Search */}
          <div
            className="relative hidden md:flex flex-1 max-w-2xl mx-4"
            ref={dropdownRef}
            onFocusCapture={() => {
              if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
              setIsSearchAreaActive(true);
            }}
            onBlurCapture={() => {
              blurTimeoutRef.current = window.setTimeout(() => {
                const activeEl = document.activeElement;
                const stillInside =
                  dropdownRef.current && activeEl && dropdownRef.current.contains(activeEl);
                // Check if we're navigating (link/button clicked)
                const isNavigating = activeEl?.tagName === 'A' || activeEl?.closest('a');
                if (!stillInside && !isNavigating) {
                  setIsSearchAreaActive(false);
                  setIsSearchInputFocused(false);
                }
              }, 150);
            }}
          >
            <div className="flex flex-1 bg-white border border-gray-200 rounded-lg ">
              {/* Ерөнхий хайлт */}
              <div className="flex items-center gap-2 flex-1 px-4">
                <input
                  type="text"
                  placeholder="Хайх..."
                  ref={searchInputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => {
                    setIsSearchInputFocused(true);
                    setIsSearchAreaActive(true);
                  }}
                  onBlur={() => {
                    if (searchBlurTimeoutRef.current) {
                      clearTimeout(searchBlurTimeoutRef.current);
                    }
                    searchBlurTimeoutRef.current = window.setTimeout(() => {
                      setIsSearchInputFocused(false);
                    }, 80);
                  }}
                  className="flex-1 py-3 text-sm focus:outline-none"
                />
              </div>

              <div className="h-10 w-px bg-gray-200 self-center" />

              {/* Байршлаар хайх (autocomplete) */}
              <div className="relative flex items-center w-[240px]">
                <input
                  type="text"
                  value={districtQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setDistrictQuery(value);

                    if (!value.trim()) {
                      setDistrictSuggestions(districts);
                      return;
                    }

                    const filtered = districts.filter((d) =>
                      d.name.toLowerCase().includes(value.toLowerCase())
                    );
                    setDistrictSuggestions(filtered);
                  }}
                  onFocus={() => {
                    if (!districtQuery.trim()) {
                      setDistrictSuggestions(districts);
                    }
                    setIsSearchInputFocused(false);
                  }}
                  placeholder="Байршлаар..."
                  className="w-full pl-4 pr-14 py-3 text-sm focus:outline-none"
                />

                {/* Clear button (X) */}
                {selectedDistrict && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedDistrict(null);
                      setDistrictQuery("");
                      setDistrictSuggestions([]);
                    }}
                    className="absolute inset-y-0 right-14 flex items-center text-gray-400 hover:text-red-500">
                    ✕
                  </button>
                )}

                {/* Search button */}
                <button
                  type="button"
                  onClick={handleSearch}
                  className="absolute inset-y-0 rounded-md right-0 flex items-center cursor-pointer justify-center px-4 bg-[#577099] text-white text-sm font-medium"
                  aria-label="Хайлт хийх"
                >
                  <FaSearch size={16} />
                </button>

                {/* Autocomplete dropdown */}
                {districtSuggestions.length > 0 && (
                  <ul className="absolute top-full mt-2 left-0 w-full bg-white rounded-lg shadow-xl z-50 max-h-60 overflow-auto border border-gray-200">
                    {districtSuggestions.map((d) => (
                      <li
                        key={d.id}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleDistrictSelect(d);
                        }}
                        onClick={() => handleDistrictSelect(d)}
                        className="px-4 py-2.5 cursor-pointer hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-sm transition-colors"
                      >
                        {d.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Dropdown */}
            {isSearchInputFocused && isSearchAreaActive && (
              <div className="absolute top-full left-0 w-full mt-2 z-50">
                <div className="bg-white rounded-lg shadow-xl overflow-hidden border border-gray-200">
                  <div className="max-h-80 overflow-y-auto overflow-x-hidden">
                    {filtered.length > 0 ? (
                      filtered.map((c) => <ResultCard key={c.id} company={c} />)
                    ) : (
                      <div className="p-6 text-center text-gray-500 text-sm">
                        <p className="mb-2">Одоогоор хайлтанд тохирох компаниуд олдсонгүй.</p>
                      </div>
                    )}
                  </div>
                  {filtered.length > 0 && (
                    <button
                      type="button"
                      style={{
                        background: 'linear-gradient(to right, #577099, #2d3a4a, #1f2837)',
                      }}
                      className="w-full px-4 py-3 text-sm font-semibold text-white hover:opacity-90 transition-all duration-300 border-t border-gray-200"
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #577099, #4a5f85, #577099)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(to right, #1f2837, #2d3a4a, #1f2837)';
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleShowAll(query, selectedDistrict?.id)}
                    >
                      Бүгдийг харах
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Nav links */}
          <div className="hidden md:flex items-center gap-6 text-sm font-medium">
            {navItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`relative px-3 py-2 text-white rounded-lg transition-all duration-200 hover:bg-[#577099] hover:text-white ${pathname === item.href ? "text-white bg-white/10" : ""}`}
              >
                {item.label}
              </Link>
            ))}
            {session && (
              <div className="relative inline-block" ref={menuRef}>
                <button
                  onClick={() => setOpen(!open)}
                  className="cursor-pointer p-1 rounded-full hover:bg-white/20 transition relative"
                >
                  {userImage ? (
                    <Image
                      src={userImage}
                      alt={session?.name || session?.user?.name || "User"}
                      width={36}
                      height={36}
                      className="rounded-full object-cover border-2 border-white/50"
                      unoptimized
                      onError={() => setUserImage(null)}
                    />
                  ) : (
                    <FaUserCircle size={36} className="text-white" />
                  )}
                </button>
                {open && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden">
                    {/* User Info Header */}
                    <div className="px-4 py-3 bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200">
                      <div className="flex items-center gap-3">
                        {userImage ? (
                          <Image
                            src={userImage}
                            alt={session?.name || session?.user?.name || "User"}
                            width={40}
                            height={40}
                            className="rounded-full object-cover"
                            unoptimized
                            onError={() => setUserImage(null)}
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                            <User className="w-5 h-5 text-white" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {session?.name || session?.user?.name || "Хэрэглэгч"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {session?.email || session?.user?.email || ""}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <ul className="py-2 text-sm text-gray-700">
                      <li>
                        <Link
                          href="/customer/profile"
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                          onClick={() => setOpen(false)}
                        >
                          <User className="w-4 h-4 text-gray-500" />
                          <span>Профайл</span>
                        </Link>
                      </li>
                      <li className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={() => {
                            signOut({ callbackUrl: "/" });
                            setOpen(false);
                          }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 text-red-600 transition-colors"
                        >
                          <LogOut className="w-4 h-4" />
                          <span>Гарах</span>
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            )}
            {PARTNER_PORTAL ? (
              <a
                href={PARTNER_PORTAL}
                className="cursor-pointer text-white bg-[#577099] flex items-center gap-1 px-3 py-3 border border-transparent rounded-lg shadow-sm transition-all duration-300 text-xs font-medium hover:shadow-md"
              >
                Бизнес портал
              </a>
            ) : (
              <Link
                href="/partner"
                className="cursor-pointer text-white bg-[#577099] flex items-center gap-1 px-3 py-3 border border-transparent rounded-lg shadow-sm transition-all duration-300 text-xs font-medium hover:shadow-md"
              >
                Бизнес портал
              </Link>
            )}
          </div>

          {/* Mobile hamburger */}
          <div className="md:hidden">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-white focus:outline-none text-2xl"
              aria-label="Mobile menu"
            >
              ☰
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {menuOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
            onClick={() => setMenuOpen(false)}
          />
          <div className="fixed inset-y-0 right-0 w-4/5 max-w-sm bg-white z-50 shadow-2xl flex flex-col p-6 gap-6 overflow-y-auto">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Image src="/chanaraas-logo.svg" alt="Chanaraas" width={36} height={36} />
                <span className="text-xl font-bold text-[#005A8B]">CHANARAAS</span>
              </div>
              <button
                onClick={() => setMenuOpen(false)}
                className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200"
                aria-label="Хаах"
              >
                ×
              </button>
            </div>

            <div className="flex flex-col gap-3">
              <input
                type="text"
                placeholder="Хайх..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="w-full border border-gray-200 rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#0053E2]"
              />
              {query && (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm max-h-64 overflow-y-auto">
                  {filtered.length > 0 ? (
                    filtered.map((c) => (
                      <ResultCard key={c.id} company={c} />
                    ))
                  ) : (
                    <div className="p-4 text-sm text-gray-500">Хайлтанд тохирох үр дүн олдсонгүй.</div>
                  )}
                  <button
                    className="w-full py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-500 to-purple-600"
                    onClick={() => {
                      handleShowAll(query, selectedDistrict?.id);
                      setMenuOpen(false);
                    }}
                  >
                    Бүгдийг харах
                  </button>
                </div>
              )}
            </div>

            <div className="flex flex-col gap-4 text-sm font-medium">
              {navItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`px-2 py-2 rounded-lg transition ${pathname === item.href ? "bg-blue-50 text-[#0053E2]" : "text-gray-700 hover:bg-gray-100"}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            {session ? (
              <div className="flex flex-col gap-3">
                <Link
                  href="/customer/profile"
                  className="w-full text-center py-3 rounded-full border border-gray-200 text-gray-700 hover:border-[#0053E2]"
                >
                  Профайл
                </Link>
                <button
                  onClick={() => signOut({ callbackUrl: "/" })}
                  className="w-full py-3 rounded-full bg-red-500 text-white font-semibold hover:bg-red-600"
                >
                  Гарах
                </button>
              </div>
            ) : (
              <button
                onClick={() => router.push("/customer")}
                className="py-3 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold"
              >
                Нэвтрэх
              </button>
            )}

            {PARTNER_PORTAL ? (
              <a
                href={PARTNER_PORTAL}
                className="mt-4 py-3 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 text-center text-sm font-medium block"
              >
                Бизнес портал
              </a>
            ) : (
              <Link
                href="/partner"
                className="mt-4 py-3 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200 text-center text-sm font-medium block"
                onClick={() => setMenuOpen(false)}
              >
                Бизнес портал
              </Link>
            )}
          </div>
        </>
      )}
    </>
  );
}

function partnerListDisplayName(company) {
  return company.company_name || company.companyName || company.name || "Компани";
}

function partnerListImageSrc(company) {
  const raw = company.image || company.logoUrl;
  if (!raw || typeof raw !== "string") return "/chanaraas-logo.svg";
  const value = raw.trim();
  if (!value || value === "null" || value === "undefined") return "/chanaraas-logo.svg";
  const normalized = value.replace(/\\/g, "/");
  if (/^https?:\/\//i.test(normalized)) return encodeURI(normalized);
  if (/^data:/i.test(normalized)) return normalized;
  const base = BASE_URL || "";
  const path = normalized.startsWith("/") ? normalized : `/${normalized}`;
  return encodeURI(base ? `${base}${path}` : path);
}

function categoryDisplayName(company) {
  if (!company?.category) return null;
  if (typeof company.category === "string") return company.category;
  return company.category.name || null;
}

function ResultCard({ company }) {
  const router = useRouter();
  const reviews = company.reviews || [];
  const displayName = partnerListDisplayName(company);
  const [imageError, setImageError] = useState(false);
  const imageSrc = imageError ? "/chanaraas-logo.svg" : partnerListImageSrc(company);
  const averageRating =
    reviews.length > 0
      ? (
          reviews.reduce((sum, r) => sum + (r.rating || 0), 0) /
          reviews.length
        ).toFixed(1)
      : "N/A";

  const handleCardClick = () => {
    router.push(`/partner/${company.id}`);
  };

  return (
    <div
      className="flex flex-col md:flex-row items-start md:items-center gap-4 p-4 bg-white border-b border-gray-100 hover:bg-gray-100 transition-all duration-200 cursor-pointer"
      role="button"
      tabIndex={0}
      onMouseDown={(e) => {
        // Only prevent default if clicking on the card itself, not on links inside
        if (e.target === e.currentTarget || !e.target.closest('a')) {
          e.preventDefault();
          handleCardClick();
        }
      }}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleCardClick();
        }
      }}
    >
      <div className="flex flex-col md:flex-row items-start md:items-center gap-4 flex-1">
        <div className="relative w-full md:w-[80px] h-[80px] overflow-hidden rounded-lg bg-gray-100">
          <img
            src={imageSrc}
            alt={displayName}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
            onError={() => setImageError(true)}
            loading="lazy"
            referrerPolicy="no-referrer"
          />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-base truncate text-gray-900 hover:text-[#577099] transition-colors">
              {displayName}
            </h3>
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            {averageRating !== "N/A" ? (
              <>
                <span>⭐ {averageRating}</span>
                <span>· {reviews.length} сэтгэгдэл</span>
              </>
            ) : (
              <span>Сэтгэгдэл байхгүй</span>
            )}
          </div>
          <div className="mt-1 text-xs text-gray-700 space-y-1 break-words">
            {company.company_email && (
              <div>
                <b>Имэйл:</b> {company.company_email}
              </div>
            )}
            {company.company_phone && (
              <div>
                <b>Утас:</b> {company.company_phone}
              </div>
            )}
            {company.address && (
              <div>
                <b>Хаяг:</b> {company.address}
              </div>
            )}
            {categoryDisplayName(company) && (
              <div>
                <b>Ангилал:</b> {categoryDisplayName(company)}
              </div>
            )}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="mt-2 md:mt-0 text-xs font-medium text-center text-[#577099] hover:text-[#4a5f85] transition-colors flex-shrink-0 whitespace-nowrap px-3 py-2 bg-transparent border-none cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          router.push(`/review/${company.id}`);
        }}
      >
        Сэтгэгдэл<br/>бичих
      </button>
    </div>
  );
}
